import { useState, useEffect, type ReactNode } from 'react';

interface ClientOnlyProps {
  children: ReactNode;
  /**
   * Optional fallback to render during SSR
   * If not provided, renders null on the server
   */
  fallback?: ReactNode;
}

/**
 * ClientOnly Component
 *
 * Renders children only on the client side, after hydration.
 * This prevents hydration mismatches when using client-only APIs or libraries.
 *
 * During SSR and initial render, it renders the fallback (or null).
 * After mounting, it renders the children.
 *
 * Usage:
 * ```tsx
 * <ClientOnly>
 *   <ComponentThatUsesWindow />
 * </ClientOnly>
 * ```
 *
 * With fallback:
 * ```tsx
 * <ClientOnly fallback={<div>Loading...</div>}>
 *   <ComponentThatUsesWindow />
 * </ClientOnly>
 * ```
 */
export function ClientOnly({ children, fallback = null }: ClientOnlyProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // During SSR and first render, show fallback
  if (!mounted) {
    return <>{fallback}</>;
  }

  // After mount, show children
  return <>{children}</>;
}
